import { Typography } from '@euler/app/components/typography';
import { OcrPreviewBottomActions } from '@euler/app/flows/common/components/OcrPreviewBottomActions';
import { VehicleServiceInfoView } from '@euler/app/flows/common/components/VehicleServiceInfoView';
import { useOcrResultServiceInfoFeature } from '@euler/app/flows/order/functions/useOcrResultServiceInfoFeature';
import {
  isValidVin,
  validateVin,
  vinTransformed,
} from '@euler/app/flows/order/functions/vinHelper';
import { OrderExtra } from '@euler/app/flows/order/types';
import { Input } from '@euler/components/form/Input';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AsyncState } from '@euler/functions/useFetchAsyncState';
import { useFormController } from '@euler/lib/form/controller';
import { VehicleServiceInfo } from '@euler/services';
import { memo, useCallback } from 'react';
import { View } from 'react-native';
import { Fill } from 'react-slot-fill';
import { BehaviorSubject } from 'rxjs';

export const VinRecognizeResultView = memo(
  ({
    result,
    orderDetail$,
    onVinChange,
    extraInfo,
    fetchExtraInfo,
    lastResult,
    onConfirm,
  }: {
    result: string;
    lastResult: string | undefined;
    orderDetail$: BehaviorSubject<OrderExtra>;
    extraInfo: AsyncState<VehicleServiceInfo | null>;
    fetchExtraInfo: (result: string) => any;
    onVinChange?: (value: string) => void;
    onConfirm?: (plateNo: string, serviceInfo?: VehicleServiceInfo) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    if (!lastResult) {
      lastResult = result;
    }

    const { form, validateAll } = useFormController({
      vin: {
        required: true,
        defaultValue: result,
        validators: [validateVin],
      },
    });

    const onEndEditing = useCallback(() => {
      if (form.vin.value !== result) {
        onVinChange?.(form.vin.value);
      }
    }, [form.vin.value, onVinChange, result]);

    const onVinBlur = useCallback(() => {
      form.vin.onBlur();
      const vin = form.vin.value;
      if (!isValidVin(vin)) {
        return;
      }
      if (vin !== lastResult) {
        fetchExtraInfo(vin);
      }
    }, [fetchExtraInfo, form.vin, lastResult]);

    const onFetchExtraInfo = useCallback(() => {
      fetchExtraInfo(result);
    }, [fetchExtraInfo, result]);

    const serviceInfoFeature = useOcrResultServiceInfoFeature(
      orderDetail$,
      extraInfo.status === 'success' ? extraInfo.result : undefined,
    );

    return (
      <>
        <View
          css={`
            padding: ${rpx(8)}px ${rpx(15)}px 0;
          `}
        >
          <Typography.Text size={rpx(14)} lineHeight={rpx(16)} color="#999">
            识别结果，点击可手动修改
          </Typography.Text>
          <Input
            bottomSheet={false}
            value={form.vin.value}
            errorMsg={form.vin.error}
            placeholder="请输入车辆VIN码"
            autoCapitalize="characters"
            keyboardType="ascii-capable"
            errorBottomOffset={2}
            onChangeText={s => form.vin.update(vinTransformed(s))}
            onBlur={onVinBlur}
            onEndEditing={onEndEditing}
            containerStyle={{
              marginTop: rpx(10),
            }}
          />
        </View>
        <VehicleServiceInfoView
          state={extraInfo}
          onFetch={onFetchExtraInfo}
          {...serviceInfoFeature}
        />
        <Fill name="ocr-preview-footer">
          <OcrPreviewBottomActions
            visible
            onConfirm={async () => {
              const isValid = await validateAll();
              if (!isValid) return;
              onConfirm?.(
                form.vin.value,
                extraInfo.status === 'success'
                  ? extraInfo.result ?? undefined
                  : undefined,
              );
            }}
          />
        </Fill>
      </>
    );
  },
);
